import { createContext, useContext, useMemo, type ReactNode } from 'react';

import { DEFAULT_BRAND, resolveBranding } from './branding';
import type { BrandConfig } from './types';

const BrandingContext = createContext<BrandConfig>(DEFAULT_BRAND);

/**
 * Reads the raw `app_id` value from the current URL query string.
 *
 * Read-only. The returned value is passed straight into `resolveBranding`'s
 * allowlist lookup and is NEVER written back into the DOM, used in
 * `innerHTML`, or evaluated — see `branding.ts` for the fail-closed
 * resolution contract.
 */
const getAppIdFromLocation = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return new URLSearchParams(window.location.search).get('app_id') ?? undefined;
};

type Props = {
  readonly children: ReactNode;
};

/**
 * Thin per-app branding layer mounted once near the app root (see `App.tsx`).
 *
 * Resolves the brand for the current `/sign-in?app_id=...` request one time
 * per page load and exposes it via `useBranding()`. Does not alter layout,
 * routing, element order, or available sign-in methods/connectors — it only
 * feeds values into existing theming slots. Logo and headline copy are
 * consumed directly from `useBranding()` by `LandingPageLayout`; the primary
 * color is consumed by the stock `useColorTheme` hook (see
 * `Providers/AppBoundary/use-color-theme.ts`), which treats the brand's
 * `primaryColor` as the source of truth over the tenant's stock
 * `experienceSettings` color — there is a single writer of the
 * `--color-brand-*` CSS custom properties, so no effect-ordering race is
 * possible between this provider and the stock theming effect.
 */
const BrandingProvider = ({ children }: Props) => {
  const brand = useMemo(() => resolveBranding(getAppIdFromLocation()), []);

  return <BrandingContext.Provider value={brand}>{children}</BrandingContext.Provider>;
};

export const useBranding = (): BrandConfig => useContext(BrandingContext);

export default BrandingProvider;
